import { useCallback, useLayoutEffect, useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import type { GuestHistory } from "../../../../api/reservations";
import type { NewReservationDraft } from "../../logic/newReservation";
import type { GuestFieldError } from "../../logic/guestFields";
import type { GuestField, ValidatedGuestField } from "../../hooks/useNewReservationForm";
import { useEscapeToClose } from "../../hooks/useEscapeToClose";
import { SectionLabel } from "./formPrimitives";
import { fieldClass, guestHistoryChipRoom } from "./formStyles";
import { GuestHistoryCard, GuestHistoryChip } from "./GuestHistory";

/** Грешката под полето („Невалиден телефонен номер"). */
const FieldError = ({ id, error }: { id: string; error: GuestFieldError | null }) => {
  const { t } = useTranslation();
  if (!error) return null;
  return (
    <div id={id} role="alert" className="-mt-1 px-1 text-[12px] font-semibold leading-[1.3] text-danger-ink">
      {t(`reservations.form.errors.${error}`)}
    </div>
  );
};

/** Червената звездичка на задължително поле — вдясно в полето, като „МАСИ *". */
const RequiredMark = () => (
  <span
    aria-hidden
    className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[14px] font-bold text-danger-ink"
  >
    *
  </span>
);

/**
 * „ГОСТ": Телефон *, Име * и Забележка. Името става с главна буква в
 * началото на всяка дума, докато се пише; грешките са под полетата (след
 * напускане или опит за запис). SMS потвърждението е скрито.
 * Познат телефон: чип „◷ 5 резервации" вдясно в полето отваря картата с
 * историята на госта под него; докато е отворена, Esc първо затваря нея.
 */
const GuestSection = ({
  draft,
  errors,
  isNarrow,
  guestHistory,
  onFieldChange,
  onFieldBlur,
}: {
  draft: NewReservationDraft;
  // Видимите грешки (null — няма или полето още не е напуснато).
  errors: Record<ValidatedGuestField, GuestFieldError | null>;
  isNarrow: boolean;
  // Историята на познатия телефон (null — непознат, невалиден или още се пише).
  guestHistory: GuestHistory | null;
  onFieldChange: (field: GuestField, value: string) => void;
  onFieldBlur: (field: ValidatedGuestField) => void;
}) => {
  const { t } = useTranslation();
  // Картата е отворена за ТОЗИ телефон — друг номер я свива сам.
  const [historyOpenFor, setHistoryOpenFor] = useState<string | null>(null);
  const historyOpen = guestHistory !== null && historyOpenFor === guestHistory.phoneKey;
  const toggleHistory = () => setHistoryOpenFor(historyOpen ? null : (guestHistory?.phoneKey ?? null));
  const closeHistory = useCallback(() => setHistoryOpenFor(null), []);
  // Esc първо затваря картата, където и да е фокусът (capture + preventDefault):
  // Safari не фокусира бутона при клик и без това същото Esc би затворило
  // цялата форма (useEscapeBlurOrClose) с написаното в нея.
  useEscapeToClose(historyOpen, closeHistory);
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Курсорът в името преди главните букви: React записва наново
  // `input.value`, щом стойността се различава от писаната, и курсорът
  // отскача в края („Мария етрова" → поправка в средата → „Мария ЕтроваП").
  const pendingNameCaret = useRef<{ start: number; end: number } | null>(null);

  useLayoutEffect(() => {
    const caret = pendingNameCaret.current;
    const input = nameInputRef.current;
    pendingNameCaret.current = null;
    if (!caret || !input || document.activeElement !== input) return;
    // Главната буква не сменя дължината — същият индекс е верен.
    input.setSelectionRange(caret.start, caret.end);
  }, [draft.guestName]);

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd, value } = event.target;
    if (selectionStart !== null && selectionEnd !== null) {
      pendingNameCaret.current = { start: selectionStart, end: selectionEnd };
    }
    onFieldChange("guestName", value);
  };

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>{t("reservations.form.guest")}</SectionLabel>
      <div className="relative">
        <input
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={draft.phone}
          maxLength={40}
          placeholder={t("reservations.form.phone")}
          aria-label={t("reservations.form.phone")}
          aria-required
          aria-invalid={errors.phone !== null}
          aria-describedby={errors.phone ? "reservation-phone-error" : undefined}
          onChange={(event) => onFieldChange("phone", event.target.value)}
          onBlur={() => onFieldBlur("phone")}
          // Място за чипа вдясно — номерът не минава под него.
          className={`${fieldClass(isNarrow, errors.phone !== null)} h-11 ${guestHistory ? guestHistoryChipRoom(isNarrow) : "pr-8"}`}
        />
        {guestHistory ? (
          <GuestHistoryChip history={guestHistory} open={historyOpen} isNarrow={isNarrow} onToggle={toggleHistory} />
        ) : (
          <RequiredMark />
        )}
      </div>
      <FieldError id="reservation-phone-error" error={errors.phone} />
      {historyOpen && guestHistory && <GuestHistoryCard history={guestHistory} />}
      <div className="relative">
        <input
          type="text"
          autoComplete="off"
          autoCapitalize="words"
          value={draft.guestName}
          maxLength={100}
          placeholder={t("reservations.form.name")}
          aria-label={t("reservations.form.name")}
          aria-required
          aria-invalid={errors.guestName !== null}
          aria-describedby={errors.guestName ? "reservation-name-error" : undefined}
          ref={nameInputRef}
          onChange={onNameChange}
          onBlur={() => onFieldBlur("guestName")}
          className={`${fieldClass(isNarrow, errors.guestName !== null)} h-11 pr-8`}
        />
        <RequiredMark />
      </div>
      <FieldError id="reservation-name-error" error={errors.guestName} />
      <textarea
        rows={2}
        value={draft.note}
        placeholder={t("reservations.form.note")}
        aria-label={t("reservations.form.note")}
        onChange={(event) => onFieldChange("note", event.target.value)}
        className={`${fieldClass(isNarrow)} resize-none py-2.5 leading-[1.4]`}
      />
    </div>
  );
};

export default GuestSection;
